import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatINR, fmtDate } from "@/lib/roles";
import { Loader2, ArrowLeft, CreditCard, Smartphone, Building2, Banknote, CheckCircle2, Landmark, Zap, Store } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function Pay() {
  const { did } = useParams();
  const nav = useNavigate();
  const [demand, setDemand] = useState(null);
  const [demands, setDemands] = useState([]);
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("upi");
  const [payment, setPayment] = useState(null);
  const [step, setStep] = useState(1); // 1: select, 2: pay, 3: success
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (did) {
      api.get(`/payments/demands/${did}`).then((r) => {
        setDemand(r.data);
        setAmount(r.data.total - (r.data.paid || 0));
        setStep(2);
      });
    } else {
      api.get("/payments/demands?status=pending&limit=50").then((r) => setDemands(r.data.items || []));
      api.get("/payments/demands?status=partial&limit=50").then((r) => setDemands((prev) => [...prev, ...(r.data.items || [])]));
    }
  }, [did]);

  const startPay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/init", { demand_id: demand.id, amount: Number(amount), method });
      setPayment(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  const confirmPay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/confirm", { payment_id: payment.id });
      setPayment(data);
      setStep(3);
      toast.success("Payment successful");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  if (!did) {
    return (
      <div className="space-y-6 max-w-3xl" data-testid="pay-list">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Payments</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold">Pay pending dues</h1>
        </div>
        <Card>
          <CardContent className="p-0">
            {demands.length === 0 ? (
              <div className="p-10 text-center text-slate-500">No pending dues.</div>
            ) : (
              <div className="divide-y">
                {demands.map((d) => (
                  <div key={d.id} className="p-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium">{d.demand_id} · <span className="capitalize">{d.module.replace("_", " ")}</span></div>
                      <div className="text-xs text-slate-500">Period {d.period} · {d.reference?.property_id || d.reference?.consumer_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-semibold">{formatINR(d.total - (d.paid || 0))}</div>
                      <Badge variant={d.status === "overdue" ? "destructive" : "secondary"} className="capitalize">{d.status}</Badge>
                    </div>
                    <Link to={`/portal/pay/${d.id}`}><Button size="sm" className="bg-[#E8702A] hover:bg-[#d95f1c]" data-testid={`btn-pay-${d.id}`}>Pay</Button></Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!demand) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const outstanding = demand.total - (demand.paid || 0);

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="pay-page">
      <div className="flex items-center gap-3">
        <Link to="/portal"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
      </div>

      {/* Step tabs */}
      <div className="flex items-center gap-2 text-xs">
        {[["1", "Demand"], ["2", "Payment"], ["3", "Receipt"]].map(([n, l]) => (
          <div key={n} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${step >= Number(n) ? "bg-[#0F2942] text-white border-[#0F2942]" : "bg-white dark:bg-slate-900 text-slate-500 border-slate-200"}`}>
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">{n}</span> {l}
          </div>
        ))}
      </div>

      {step === 2 && (
        <>
          <Card data-testid="demand-summary-card">
            <CardHeader><CardTitle className="text-base font-heading">Demand summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div className="text-slate-500">Demand ID</div><div className="font-mono">{demand.demand_id}</div>
                <div className="text-slate-500">Module</div><div className="capitalize">{demand.module.replace("_", " ")}</div>
                <div className="text-slate-500">Period</div><div>{demand.period}</div>
                <div className="text-slate-500">Reference</div><div className="font-mono text-xs">{demand.reference?.property_id || demand.reference?.consumer_id}</div>
                <div className="text-slate-500">Due date</div><div>{fmtDate(demand.due_date)}</div>
                <div className="text-slate-500">Principal</div><div className="font-mono">{formatINR(demand.principal)}</div>
                <div className="text-slate-500">Penalty</div><div className="font-mono">{formatINR(demand.penalty)}</div>
                <div className="text-slate-500">Paid</div><div className="font-mono">{formatINR(demand.paid || 0)}</div>
                <div className="text-slate-500 border-t pt-2">Outstanding</div>
                <div className="border-t pt-2 font-mono font-bold text-lg text-[#0F2942] dark:text-white">{formatINR(outstanding)}</div>
              </div>
            </CardContent>
          </Card>

          {!payment ? (
            <Card data-testid="payment-method-card">
              <CardHeader><CardTitle className="text-base font-heading">Choose payment method</CardTitle></CardHeader>
              <CardContent>
                <div>
                  <Label>Amount to pay</Label>
                  <Input type="number" min={1} max={outstanding} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" />
                </div>
                <div className="mt-4">
                  <Label>Method</Label>
                  <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {[
                      { id: "upi", icon: Smartphone, label: "QR" },
                      { id: "card", icon: CreditCard, label: "Card" },
                      { id: "netbanking", icon: Building2, label: "Online transfer" },
                      { id: "bbps", icon: Zap, label: "Bill aggregator" },
                      { id: "cash", icon: Store, label: "Service counter" },
                      { id: "cheque", icon: Banknote, label: "Cheque / DD" },
                    ].map((m) => (
                      <label key={m.id} className={`cursor-pointer flex flex-col items-center gap-2 p-4 rounded-lg border ${method === m.id ? "border-[#0F2942] bg-[#0F2942]/5" : "border-slate-200 dark:border-slate-700"}`} data-testid={`method-${m.id}`}>
                        <RadioGroupItem value={m.id} className="sr-only" />
                        <m.icon className="w-5 h-5 text-[#0F2942]" strokeWidth={1.75} />
                        <span className="text-xs">{m.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <Button className="mt-6 w-full bg-[#E8702A] hover:bg-[#d95f1c]" onClick={startPay} disabled={loading || !amount} data-testid="btn-start-payment">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Settle ${formatINR(amount)}`}
                </Button>
                <p className="mt-3 text-xs text-slate-500 text-center">Simulated settlement — no funds are transferred.</p>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="payment-processing-card">
              <CardHeader><CardTitle className="text-base font-heading">Complete settlement</CardTitle></CardHeader>
              <CardContent className="text-center space-y-4">
                {method === "upi" && payment.qr_data && (
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-4 bg-white rounded-xl border">
                      <QRCodeSVG value={payment.qr_data} size={200} data-testid="qr-code" />
                    </div>
                    <div className="text-xs text-slate-500">Reference QR · Amount {formatINR(payment.amount)}</div>
                    <div className="font-mono text-xs text-slate-600">{payment.payment_id}</div>
                  </div>
                )}
                {method !== "upi" && (
                  <div className="p-6">
                    <div className="text-slate-500 mb-2">Processing via {method.toUpperCase()}</div>
                    <div className="font-mono text-xs">{payment.payment_id}</div>
                  </div>
                )}
                <Button className="w-full bg-[#0D7650] hover:bg-[#0a5d3f]" onClick={confirmPay} disabled={loading} data-testid="btn-confirm-payment">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simulate payment success"}
                </Button>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {step === 3 && payment && (
        <Card data-testid="payment-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#0D7650]/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#0D7650]" />
            </div>
            <h2 className="text-2xl font-heading font-bold mt-4">Payment successful</h2>
            <p className="text-slate-500 mt-2">Your payment has been recorded.</p>
            <div className="mt-6 grid grid-cols-2 gap-y-2 text-sm text-left max-w-md mx-auto">
              <div className="text-slate-500">Receipt</div><div className="font-mono">{payment.receipt_number}</div>
              <div className="text-slate-500">Payment ID</div><div className="font-mono">{payment.payment_id}</div>
              <div className="text-slate-500">Txn Ref</div><div className="font-mono">{payment.txn_ref}</div>
              <div className="text-slate-500">Amount</div><div className="font-mono font-semibold">{formatINR(payment.amount)}</div>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <Link to={`/receipts/${payment.receipt_number}`}><Button className="bg-[#0F2942]" data-testid="btn-view-receipt">View receipt</Button></Link>
              <Button variant="outline" onClick={() => nav("/portal")}>Back to dashboard</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
